// PlayFirst — Super-admin to-do notification numbers manager.
// Renders inside /admin/todos. Any super-admin can SEE the list (so they
// understand who'll be pinged on task completion); only the PRIMARY
// super-admin can edit it. This component fetches once on mount, hides
// itself if you're not even a super-admin (the parent already guards
// that), and gracefully hides the editor for non-primary admins.
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Phone, Plus, Trash2, MessageCircle, Loader2, Save, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const auth = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

export default function TodoNotificationSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null); // phone currently being tested
  const [isPrimary, setIsPrimary] = useState(false);
  const [primaryEmail, setPrimaryEmail] = useState('');
  const [contacts, setContacts] = useState([]); // [{label, phone}]
  const [draft, setDraft] = useState({ label: '', phone: '' });

  const sendTest = async (phone) => {
    setTesting(phone);
    try {
      const r = await axios.post(
        `${API}/admin/todos/notification-numbers/${phone}/test`,
        {},
        auth(),
      );
      if (r.data?.status === 'sent') {
        toast.success(`Test message sent to +${phone}`);
      } else if (r.data?.status === 'outside_24h_window') {
        toast.error(
          'WhatsApp 24h-window block: ask them to message the bot once, then try again.',
          { duration: 8000 },
        );
      } else {
        toast.error(`Send failed: ${r.data?.error || 'unknown error'}`);
      }
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error('Only the primary super-admin can send test messages');
      } else {
        toast.error('Test send failed — check backend logs');
      }
    } finally {
      setTesting(null);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/todos/notification-numbers`, auth());
      setContacts(r.data?.contacts || []);
      setIsPrimary(!!r.data?.is_primary_super_admin);
      setPrimaryEmail(r.data?.primary_super_admin_email || '');
    } catch (e) {
      if (e?.response?.status === 403) {
        // Not a super-admin at all — parent will handle nav.
      } else {
        toast.error("Couldn't load notification numbers");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, []);

  const save = async (next) => {
    setSaving(true);
    try {
      const r = await axios.put(
        `${API}/admin/todos/notification-numbers`,
        { contacts: next },
        auth(),
      );
      setContacts(r.data?.contacts || []);
      toast.success('Saved');
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error('Only the primary super-admin can edit this');
      } else {
        toast.error("Couldn't save notification numbers");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = (e) => {
    e?.preventDefault?.();
    const phone = (draft.phone || '').replace(/[\s\-()]/g, '').replace(/^\+/, '');
    if (!phone || phone.length < 8 || !/^\d+$/.test(phone)) {
      toast.error('Enter a valid phone in international format (e.g. 27821234567)');
      return;
    }
    if (contacts.some((c) => c.phone === phone)) {
      toast.error('That number is already on the list');
      return;
    }
    const next = [...contacts, { label: (draft.label || '').trim(), phone }];
    setDraft({ label: '', phone: '' });
    save(next);
  };

  const handleRemove = (phone) => {
    const next = contacts.filter((c) => c.phone !== phone);
    save(next);
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-stone-200 bg-white p-4" data-testid="todos-notify-loading">
        <Loader2 className="w-4 h-4 animate-spin text-stone-400" />
      </div>
    );
  }

  return (
    <section
      className="rounded-2xl border border-stone-200 bg-white p-4"
      data-testid="todos-notify-card"
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center flex-shrink-0">
          <MessageCircle className="w-4 h-4" />
        </div>
        <div className="flex-1">
          <h2 className="text-base font-semibold text-stone-900">
            WhatsApp on completions
          </h2>
          <p className="text-xs text-stone-500 mt-0.5">
            When anyone ticks a task off, every number here gets a WhatsApp message
            with what was done and what&apos;s still open.
            {!isPrimary && primaryEmail && (
              <>
                {' '}Only{' '}
                <span className="font-medium text-stone-700">{primaryEmail}</span>{' '}
                can edit this list.
              </>
            )}
          </p>
        </div>
      </div>

      {/* Existing list */}
      {contacts.length === 0 ? (
        <p
          className="text-sm text-stone-500 italic px-1 py-3"
          data-testid="todos-notify-empty"
        >
          No numbers yet — completions won&apos;t ping anyone on WhatsApp.
        </p>
      ) : (
        <ul className="space-y-1.5 mb-3" data-testid="todos-notify-list">
          {contacts.map((c) => (
            <li
              key={c.phone}
              className="flex items-center justify-between gap-2 px-3 py-2 rounded-xl bg-stone-50 border border-stone-100"
              data-testid={`todos-notify-row-${c.phone}`}
            >
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <Phone className="w-3.5 h-3.5 text-stone-400 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-stone-900 truncate">
                    {c.label || <span className="text-stone-400">No label</span>}
                  </p>
                  <p className="text-xs text-stone-500 font-mono">+{c.phone}</p>
                </div>
              </div>
              {isPrimary && (
                <div className="flex items-center gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => sendTest(c.phone)}
                    disabled={testing === c.phone || saving}
                    className="text-[#7C6F9C] hover:bg-[#7C6F9C]/10"
                    data-testid={`todos-notify-test-${c.phone}`}
                    title="Send a test WhatsApp to this number now"
                  >
                    {testing === c.phone ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Send className="w-3.5 h-3.5" />
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleRemove(c.phone)}
                    disabled={saving}
                    className="text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                    data-testid={`todos-notify-remove-${c.phone}`}
                    title="Remove"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Add new (primary-only) */}
      {isPrimary && (
        <form
          onSubmit={handleAdd}
          className="flex flex-col sm:flex-row gap-2"
          data-testid="todos-notify-add-form"
        >
          <Input
            placeholder="Name (e.g. Michaela)"
            value={draft.label}
            onChange={(e) => setDraft((d) => ({ ...d, label: e.target.value }))}
            className="sm:flex-1"
            data-testid="todos-notify-new-label"
          />
          <Input
            placeholder="27821234567"
            value={draft.phone}
            onChange={(e) => setDraft((d) => ({ ...d, phone: e.target.value }))}
            className="sm:flex-1 font-mono"
            data-testid="todos-notify-new-phone"
            inputMode="tel"
          />
          <Button
            type="submit"
            disabled={saving}
            className="bg-[#7C6F9C] hover:bg-[#6a5d8a] inline-flex items-center gap-1"
            data-testid="todos-notify-add-submit"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Add number
          </Button>
        </form>
      )}

      {isPrimary && (
        <p className="text-[11px] text-stone-400 mt-2 flex items-center gap-1">
          <Save className="w-3 h-3" /> Numbers in international format, digits only — country code first (e.g. 27 for South Africa, 1 for US).
        </p>
      )}

      <div
        className="mt-3 rounded-xl bg-amber-50/60 border border-amber-200/70 p-3 text-[11px] text-amber-900 leading-relaxed"
        data-testid="todos-notify-window-explainer"
      >
        <strong>Heads up — WhatsApp&apos;s 24h rule:</strong> Meta only allows free-text WhatsApp
        messages within 24 hours of the recipient messaging your business number. If
        only your own number is receiving completion pings, ask each teammate to
        send any message (e.g. &quot;hi&quot;) to your PlayFirst WhatsApp bot once. Then hit
        the <Send className="w-3 h-3 inline -mt-0.5" /> test button next to their
        number to verify delivery.
      </div>
    </section>
  );
}
